import React, { useCallback, useEffect, useState } from "react";
import Keyboard from "./components/Keyboard";
import { stringToKeyboardKeys, type KeyStroke } from "./components/Keyboard";
import { Button, Drawer, Layout, Space, Statistic } from "antd";
import "./App.less";
import { Header, Content } from "antd/es/layout/layout";
import {
  ClearOutlined,
  ClockCircleOutlined,
  FundOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  TranslationOutlined,
} from "@ant-design/icons";
import CharacterSequence from "./components/CharacterSequence";
import { useLiveQuery } from "dexie-react-hooks";
import { store } from "./Store";
import CreateNewButton from "./components/CreateNewButton";
import { CountUpTimer } from "./components/CountUp";
import { api } from "./notification";
import json from "./version.json";
import { trace } from "./behavior";

const defaultText =
  "爸爸、妈妈、爷爷、奶奶、哥哥、姐姐、弟弟、妹妹、老师、同学、朋友、医生、警察、书包、铅笔、橡皮、尺子、课本、笔记本、杯子、牙刷、毛巾、鞋子、衣服、帽子、汽车、公交车、自行车、飞机、火车、小猫、小狗、小鱼、大树、花朵、小草、红色、黄色、蓝色、绿色、白色、黑色、圆形、方形、三角形、长方形、走、跑、跳、写、读、画、吃、喝、笑、哭、抱、拍、拉、推、扶、一、二、三、四、五、六、七、八、九、十、个、朵、只、本、支、片、太阳、月亮、星星、云、雨、风、雪、春天、夏天、秋天、冬天、上、下、左、右、前、后、里、外、早上、中午、晚上、今天、明天、昨天";

let textarea: HTMLTextAreaElement | null = null;
let createNew: Function;

const isInStandaloneMode = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  (window.navigator as any).standalone ||
  document.referrer.includes("android-app://");

function openUrl(url: string) {
  try {
    const uri = new URL(url);
    window.open(
      url,
      uri.host,
      isInStandaloneMode()
        ? "toolbar=no,scrollbars=yes,location=no,titlebar=no,status=no"
        : undefined
    );
  } catch (err) {
    console.error(err);
  }
}

function showCode(onClose?: () => void) {
  api.info({
    key: "code",
    message: "编程启蒙课",
    description: "和果壳老师一起上一堂有趣的编程课~~",
    duration: null,
    icon: <div className="code_logo"></div>,
    className: "code_notification",
    onClose,
    actions: (
      <Space>
        <Button
          size="small"
          onClick={() => {
            api.destroy("code");
            store.setOption("closeAt", Date.now());
          }}
        >
          取消
        </Button>
        <Button
          type="primary"
          className="notification-btn"
          size="small"
          onClick={() => {
            api.destroy("code");
            trace("click", {
              name: "打开编程课",
              from: 'notification'
            });
            openUrl("https://code.quguoke.com");
          }}
        >
          打开编程课
        </Button>
      </Space>
    ),
  });
}

function showPractice(onClose?: () => void) {
  api.info({
    key: "practice",
    message: "键盘指法练习",
    description: "儿童键盘指法启蒙练习~~",
    duration: null,
    icon: <div className="practice_logo"></div>,
    className: "practice_notification",
    onClose,
    actions: (
      <Space>
        <Button
          size="small"
          onClick={() => {
            api.destroy("practice");
            store.setOption("closeAt", Date.now());
          }}
        >
          取消
        </Button>
        <Button
          type="primary"
          className="notification-btn"
          size="small"
          onClick={() => {
            api.destroy("practice");
            trace("click", {
              name: "打开键盘指法练习",
              from: 'notification'
            });
            openUrl(
              window.location.protocol +
                "//" +
                window.location.host +
                "/Practice-guankalist.html"
            );
          }}
        >
          打开键盘指法练习
        </Button>
      </Space>
    ),
  });
}

function showExamPaper(onClose?: () => void) {
  api.info({
    key: "exam_paper",
    message: "趣果壳试卷",
    description: "果壳AI老师帮你一键生成练习题~~",
    duration: null,
    icon: <div className="exampaper_logo"></div>,
    className: "exam_paper_notification",
    onClose,
    actions: (
      <Space>
        <Button
          size="small"
          onClick={() => {
            api.destroy("exam_paper");
            store.setOption("closeAt", Date.now());
          }}
        >
          取消
        </Button>
        <Button
          type="primary"
          className="notification-btn"
          size="small"
          onClick={() => {
            api.destroy("exam_paper");
            trace("click", {
              name: "打开趣果壳试卷",
              from: 'notification'
            });
            openUrl("https://ep.quguoke.com");
          }}
        >
          打开趣果壳试卷
        </Button>
      </Space>
    ),
  });
}

function showNotebook(onClose?: () => void) {
  api.info({
    key: "notebook",
    message: "趣果壳记事本",
    description: "学会打字后就可以自由记录啦~~",
    duration: null,
    icon: <div className="notebook_logo"></div>,
    className: "exam_paper_notification",
    onClose,
    actions: (
      <Space>
        <Button
          size="small"
          onClick={() => {
            api.destroy("notebook");
            store.setOption("closeAt", Date.now());
          }}
        >
          取消
        </Button>
        <Button
          type="primary"
          className="notification-btn"
          size="small"
          onClick={() => {
            api.destroy("notebook");
            trace("click", {
              name: "打开趣果壳记事本",
              from: "notification",
            });
            openUrl("https://note.quguoke.com");
          }}
        >
          打开记事本
        </Button>
      </Space>
    ),
  });
}

const App: React.FC = () => {
  const options = useLiveQuery(() => store.getOptions());
  const keyLog = useLiveQuery(() => store.getLogs());
  const statisic = useLiveQuery(() => store.getStatics());
  const [keyCodes, setKeyCodes] = useState<KeyStroke[]>([]); // 用于存储按键列表
  const [targetKeyIndex, setTargetKeyIndex] = useState(0);
  const [composingIndex, setComposingIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const [inputting, setInputting] = useState(false);
  const reportedRef = React.useRef(false);

  const handleKeyPress = (
    key: { label: string; code: string },
    isCorrect: boolean | null
  ) => {
    store.addLog(
      key.label,
      isCorrect === true ? "correct" : isCorrect === false ? "wrong" : null
    );
  };

  const handleFocus = useCallback(() => {
    if (textarea) {
      // 获取文本长度
      const length = textarea.value.length;

      // 设置光标位置到文本末尾
      textarea.selectionStart = length;
      textarea.selectionEnd = length;
      textarea.focus();
    }
  }, []);

  const handleKeyInit = useCallback(
    (ele: any) => {
      textarea = ele;
      handleFocus();
    },
    [handleFocus]
  );

  const handleInit = useCallback(
    (chars: any[]) => {
      setKeyCodes(chars.flatMap((c) => stringToKeyboardKeys(c.keys || c.char)));
      reportedRef.current = false;
    },
    [setKeyCodes]
  );

  const handleCurrentCharChange = useCallback((index: number, chars: any[]) => {
    console.log("Current char index:", index);
    setTargetKeyIndex(
      chars
        .slice(0, index)
        .reduce(
          (count, c) => count + stringToKeyboardKeys(c.keys || c.char).length,
          0
        )
    );
  }, []);

  const handleCountChange = useCallback((count: number) => {
    store.setOption("countUp", count);
  }, []);

  useEffect(() => {
    let timeoutId: any;

    function resetTimer() {
      // 清除之前的计时器
      if (timeoutId) clearTimeout(timeoutId);

      // 设置新的计时器
      timeoutId = setTimeout(async () => {
        const opt = await store.getOptions();
        // 24小时提示一次
        if (opt?.closeAt && Date.now() - opt?.closeAt < 24 * 60 * 60 * 1000) {
          resetTimer();
          return;
        }
        showExamPaper(() => {
          // resetTimer();
        });
      }, 30000);
    }

    // 监听键盘和鼠标事件
    document.addEventListener("keydown", resetTimer);
    document.addEventListener("mousemove", resetTimer);
    document.addEventListener("click", resetTimer);
    document.addEventListener("scroll", resetTimer);
    document.addEventListener("touchstart", resetTimer);

    // 初始化计时器
    resetTimer();

    // 返回一个清理函数，用于移除事件监听
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("keydown", resetTimer);
      document.removeEventListener("mousemove", resetTimer);
      document.removeEventListener("click", resetTimer);
      document.removeEventListener("scroll", resetTimer);
      document.removeEventListener("touchstart", resetTimer);
    };
  }, []);

  const handleComplete = useCallback(() => { 
    api.success({
      key: "success",
      message: "恭喜",
      description: "打字练习完成啦!",
      actions: (
        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => {
              api.destroy("success");
              createNew();
            }}
          >
            新建练习
          </Button>
        </Space>
      ),
    });
    if (!reportedRef.current) {
      trace("complete", { name: "完成" });
      reportedRef.current = true;
    }
  }, []);

  if (!options) return null;

  return (
    <Layout className="app-container">
      <Header>
        <div className="logo">
          <h1>
            {/* <img src={require("./logo.png")} alt="Logo" /> */}
            <span>打字练习</span>
          </h1>
          <Space>
            <CreateNewButton
              onCreate={(text, availableKeyCodes) => {
                store.setOption("inputText", "");
                store.setOption("text", text);
                store.setOption("availableKeyCodes", availableKeyCodes);
                store.clearLog();
              }}
              onInit={(showModel) => (createNew = showModel)}
            ></CreateNewButton>
          </Space>
        </div>
        <Space className="statisic-top">
          {options!.showStatisic && (
            <Statistic
              title={<span>正确</span>}
              value={statisic?.[0] || 0}
              suffix={
                <ClearOutlined
                  className="clear-icon"
                  onClick={() => store.clearLog("correct")}
                />
              }
              className="correct"
            />
          )}
          {options!.showStatisic && (
            <Statistic
              title={<span>错误</span>}
              value={statisic?.[1] || 0}
              suffix={
                <ClearOutlined
                  className="clear-icon"
                  onClick={() => store.clearLog("wrong")}
                />
              }
              className="wrong"
            />
          )}
          {options!.showCountUp && (
            <CountUpTimer
              enabled={inputting}
              count={options.countUp}
              onChange={handleCountChange}
            ></CountUpTimer>
          )}
        </Space>
        <Space className="app-btns">
          <Button
            onClick={() => {
              store.setOption("showStatisic", !options!.showStatisic);
              trace("click", {
                name: "正确率统计",
                value: !options?.showStatisic,
              });
            }}
            icon={<FundOutlined />}
            title="正确率统计"
            type="text"
          ></Button>
          <Button
            onClick={() => {
              store.setOption("showCountUp", !options!.showCountUp);
              trace("click", { name: "计时器", value: !options?.showCountUp });
            }}
            icon={<ClockCircleOutlined />}
            title="计时器"
            type="text"
          ></Button>
          <Button
            onClick={() => {
              store.setOption("showPinyin", !options!.showPinyin);
              trace("click", {
                name: "切换拼音显示",
                value: !options?.showPinyin,
              });
            }}
            icon={<TranslationOutlined />}
            title="切换拼音显示"
            type="text"
          ></Button>
          <Button
            onClick={() => {
              store.setOption("showKeyboard", !options!.showKeyboard);
              trace("click", {
                name: "切换键盘显示",
                value: !options?.showKeyboard,
              });
            }}
            icon={<span className="keyboard-icon" />}
            title="切换键盘显示"
            type="text"
          ></Button>
          <Button
            onClick={() => {
              store.setOption("showAvailableKeys", !options!.showAvailableKeys);
              store.setOption("showTargetKey", !options!.showTargetKey);
              trace("click", {
                name: "切换按键提示",
                value: !options?.showAvailableKeys,
              });
            }}
            icon={<InfoCircleOutlined />}
            title="切换按键提示"
            type="text"
          ></Button>
          <Button
            onClick={() => {
              setOpen(true);
              trace("click", { name: "查看按键记录" });
            }}
            icon={<HistoryOutlined />}
            title="查看按键记录"
            type="text"
          ></Button>
          <Button
            className="code-btn"
            icon={<div className="practice_logo small"></div>}
            title="打开键盘指法练习"
            type="text"
            onClick={() => {
              showPractice();
              trace("click", { name: "打开键盘指法练习" });
            }}
          ></Button>
          <Button
            className="code-btn"
            icon={<div className="code_logo small"></div>}
            title="打开编程课"
            type="text"
            onClick={() => {
              showCode();
              trace("click", { name: "打开编程课" });
            }}
          ></Button>
          <Button
            className="code-btn"
            icon={<div className="exampaper_logo small"></div>}
            title="打开趣果壳试卷"
            type="text"
            onClick={() => {
              showExamPaper();
              trace("click", { name: "打开趣果壳试卷" });
            }}
          ></Button>
          <Button
            className="code-btn"
            icon={<div className="notebook_logo small"></div>}
            title="打开趣果壳记事本"
            type="text"
            onClick={() => {
              showNotebook();
              trace("click", { name: "打开趣果壳记事本" });
            }}
          ></Button>
        </Space>
      </Header>
      <Content onClick={handleFocus}>
        <CharacterSequence
          text={options?.text || defaultText}
          inputtedChars={options?.inputText || ""}
          showPinyin={options?.showPinyin !== false}
          onInit={handleInit}
          onCurrentCharChange={handleCurrentCharChange}
          onComplete={handleComplete}
        ></CharacterSequence>
        <Keyboard
          value={options?.inputText || ""}
          height={options?.height || 60}
          onChange={(txt) => {
            store.setOption("inputText", txt);
            setComposingIndex(0);
          }}
          onComposingChnage={(txt) => {
            console.log("Composing change:", txt, options?.inputText);
            setComposingIndex(
              txt.replaceAll("'", "").length - (options?.inputText || "").length
            );
          }}
          onKeyPress={handleKeyPress}
          onInit={handleKeyInit}
          onHeightChange={(height) => {
            store.setOption("height", height);
          }}
          onFocus={() => setInputting(true)}
          onBlur={() => setInputting(false)}
          targetKeyCode={keyCodes[targetKeyIndex + composingIndex]}
          availableKeyCodes={options?.availableKeyCodes}
          showKeyboard={options?.showKeyboard}
          showAvailableKeys={options?.showAvailableKeys}
          showTargetKey={options?.showTargetKey}
        />
        <div className="app-footer">v {json.version}</div>
      </Content>
      <Drawer
        title="按键记录"
        closable={{ "aria-label": "Close Button" }}
        onClose={() => {
          setOpen(false);
        }}
        open={open}
      >
        <ul>
          {keyLog?.map((log, index) => {
            let className = "";
            let statusText = "";

            if (log.status === "correct") {
              className = "correct-log";
              statusText = "正确";
            } else if (log.status === "wrong") {
              className = "wrong-log";
              statusText = "错误";
            } else {
              className = "available-log";
              statusText = "可用";
            }

            return (
              <li key={index} className={className}>
                {log.key} - {statusText}
              </li>
            );
          })}
        </ul>
      </Drawer>
    </Layout>
  );
};
export default App;
